import {
  ApErrorParams,
  SwaggerAudience,
  SwaggerDefinition,
  SwaggerDefinitionStatus,
  SwaggerSourceType,
} from '@activepieces/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { t } from 'i18next';
import { ArrowLeft, RefreshCw, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { ConfirmationDeleteDialog } from '@/components/delete-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CopyButton } from '@/components/ui/copy-button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LoadingScreen } from '@/components/ui/loading-screen';
import { Separator } from '@/components/ui/separator';
import { LoadingSpinner } from '@/components/ui/spinner';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { toast } from '@/components/ui/use-toast';
import { SwaggerAudienceField } from '@/features/internal-admin/components/swagger-audience-field';
import { SwaggerRoutesPicker } from '@/features/internal-admin/components/swagger-routes-picker';
import { adminApi } from '@/features/internal-admin/lib/admin-api';
import { SwaggerStatusBadge } from '@/features/internal-admin/lib/admin-swaggers-table';
import { api } from '@/lib/api';
import { cn, formatUtils } from '@/lib/utils';

const serverMessage = (error: unknown, fallback: string): string => {
  if (!api.isError(error)) {
    return fallback;
  }
  const apError = error.response?.data as ApErrorParams | undefined;
  if (
    apError !== undefined &&
    typeof apError.params === 'object' &&
    apError.params !== null &&
    'message' in apError.params
  ) {
    return String(apError.params.message);
  }
  return fallback;
};

const InfoLine = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div className="flex items-center justify-between gap-4 py-2 text-sm">
    <span className="text-muted-foreground">{label}</span>
    <span className="min-w-0 truncate text-right font-medium">{children}</span>
  </div>
);

const AdminSwaggerDetailPage = () => {
  // Defaulted rather than asserted: the route always carries the parameter, and
  // a non-null assertion on every call site would be four places trusting that
  // instead of one.
  const { swaggerId = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    data: definition,
    isLoading,
    isError,
  } = useQuery<SwaggerDefinition, Error>({
    queryKey: ['admin-swagger', swaggerId],
    queryFn: () => adminApi.getSwagger(swaggerId),
    enabled: swaggerId.length > 0,
    // This page is a form: every field below is seeded from the response, so a
    // refetch triggered by anything other than an explicit save or refresh
    // would overwrite what the administrator had typed or unticked. Coming back
    // to the tab is enough to trigger one by default. The saves invalidate the
    // key themselves, which is what re-seeds the form.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: Infinity,
  });

  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [audience, setAudience] = useState<SwaggerAudience>(
    SwaggerAudience.CLIENTS,
  );
  const [tenantIds, setTenantIds] = useState<string[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Re-seeded whenever the server state changes (initial load, save, refresh)
  // so the form never keeps showing values the server has already replaced.
  useEffect(() => {
    if (definition === undefined) {
      return;
    }
    setDisplayName(definition.displayName);
    setDescription(definition.description);
    setLogoUrl(definition.logoUrl ?? '');
    setBaseUrl(definition.baseUrl);
    setAudience(definition.audience);
    setTenantIds(definition.tenantIds);
    setSelected(
      new Set(
        definition.operations
          .filter((operation) => operation.enabled)
          .map((operation) => operation.actionName),
      ),
    );
  }, [definition]);

  const invalidate = () => {
    return Promise.all([
      queryClient.invalidateQueries({ queryKey: ['admin-swagger', swaggerId] }),
      queryClient.invalidateQueries({ queryKey: ['admin-swaggers'] }),
    ]);
  };

  const { mutate: save, isPending: isSaving } = useMutation({
    mutationFn: (status?: SwaggerDefinitionStatus) =>
      adminApi.updateSwagger(swaggerId, {
        displayName,
        description,
        logoUrl: logoUrl.trim().length > 0 ? logoUrl.trim() : null,
        // Omitted rather than sent empty: the server refuses to clear a base
        // URL, so sending '' would make every unrelated save — renaming,
        // ticking a route — fail on a document that declares no `servers`,
        // which is precisely the case this field exists to repair.
        ...(baseUrl.trim().length > 0 ? { baseUrl: baseUrl.trim() } : {}),
        audience,
        tenantIds,
        enabledActionNames: [...selected],
        ...(status === undefined ? {} : { status }),
      }),
    onSuccess: async () => {
      // Awaited so isSaving stays true (button stays disabled/dimmed) until
      // the refetched definition lands — the undim and the label crossfade
      // then land in the same render instead of firing as two separate pops.
      await invalidate();
      toast({ variant: 'success', title: t('Your changes have been saved.') });
    },
    onError: (error) => {
      toast({
        title: t('Save failed'),
        description: serverMessage(
          error,
          t('Something went wrong. Please try again.'),
        ),
        variant: 'destructive',
      });
    },
  });

  const { mutate: refresh, isPending: isRefreshing } = useMutation({
    mutationFn: () => adminApi.refreshSwagger(swaggerId),
    onSuccess: (updated) => {
      invalidate();
      const missing = updated.operations.filter(
        (operation) => !operation.present,
      ).length;
      toast({
        variant: 'success',
        title: t('Document refreshed'),
        description:
          missing > 0
            ? t(
                '{count, plural, one {# route no longer exists in the source document.} other {# routes no longer exist in the source document.}}',
                { count: missing },
              )
            : t('Routes and metadata are up to date.'),
      });
    },
    onError: (error) => {
      toast({
        title: t('Refresh failed'),
        description: serverMessage(
          error,
          t('Something went wrong. Please try again.'),
        ),
        variant: 'destructive',
      });
    },
  });

  const isBusy = isSaving || isRefreshing;

  const enableBlockedReason = useMemo(() => {
    if (audience === SwaggerAudience.CLIENTS && tenantIds.length === 0) {
      return t('Assign at least one client before enabling this Swagger.');
    }
    if (selected.size === 0) {
      return t('Select at least one route before enabling this Swagger.');
    }
    // The field looks optional — it is, at import — but a document that
    // declares no `servers` leaves it empty, and every action would then point
    // at a host-less path. The server refuses the same case; catching it here
    // keeps the switch from looking broken.
    if (baseUrl.trim().length === 0) {
      return t('Set a base URL before enabling this Swagger.');
    }
    return null;
  }, [audience, tenantIds.length, selected.size, baseUrl]);

  // The definition carries the name and the client number of every client it
  // is assigned to, so the picker can describe them without one lookup per
  // assignment — including the ones no page of the client list has loaded.
  const knownClients = useMemo(() => {
    if (definition === undefined) {
      return undefined;
    }
    return Object.fromEntries(
      definition.tenantIds.map((tenantId, index) => [
        tenantId,
        {
          name: definition.tenantNames[index] ?? tenantId,
          number: definition.tenantCustomerIds[index] ?? tenantId,
        },
      ]),
    );
  }, [definition]);

  if (isLoading) {
    return <LoadingScreen mode="container" />;
  }

  if (isError || definition === undefined) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-destructive">{t('Swagger not found')}</p>
      </div>
    );
  }

  const isEnabled = definition.status === SwaggerDefinitionStatus.ENABLED;

  return (
    <div className="flex w-full flex-col gap-5 p-6">
      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/internal-admin/swaggers')}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-2xl font-semibold">{definition.displayName}</h1>

        {/* Status + its control live together next to the title: this is
            the primary state of the resource, not a peer of the utility
            actions (refresh/delete/save) grouped on the right. */}
        <div className="flex items-center gap-3">
          {/* The tooltip shows whatever the state: what disabling means is
              worth saying before the switch is flipped, not only while
              enabling is blocked — in which case it also says why. The
              wrapper is the trigger because a disabled switch gets no
              pointer events of its own. */}
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex">
                <Switch
                  checked={isEnabled}
                  disabled={
                    isBusy || (!isEnabled && enableBlockedReason !== null)
                  }
                  onCheckedChange={(checked) =>
                    save(
                      checked
                        ? SwaggerDefinitionStatus.ENABLED
                        : SwaggerDefinitionStatus.DISABLED,
                    )
                  }
                  aria-label={t('Enabled')}
                />
              </span>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="flex flex-col gap-1">
              {!isEnabled && enableBlockedReason !== null && (
                <p>{enableBlockedReason}</p>
              )}
              <p>{t("A disabled Swagger can't be used by any client.")}</p>
            </TooltipContent>
          </Tooltip>

          {/* The badge is the switch's visible label: the state reads at a
              glance without hovering anything. */}
          <SwaggerStatusBadge status={definition.status} />
        </div>

        <div className="ml-auto flex items-center gap-2">
          {definition.sourceType === SwaggerSourceType.URL && (
            <Button
              variant="outline"
              size="sm"
              disabled={isBusy}
              onClick={() => refresh()}
            >
              {isRefreshing ? (
                <LoadingSpinner className="mr-2 h-4 w-4" />
              ) : (
                <RefreshCw className="mr-2 h-4 w-4" />
              )}
              {t('Refresh from source')}
            </Button>
          )}
          {/* Deletion drops the routes and the client assignments with the
              definition, so it goes through the shared confirmation dialog
              rather than firing on a single click. */}
          <ConfirmationDeleteDialog
            title={t('Delete Swagger')}
            message={t(
              'Are you sure you want to delete "{displayName}"? Its routes and client assignments will be removed. This action cannot be undone.',
              { displayName: definition.displayName },
            )}
            entityName={t('Swagger')}
            isDanger={true}
            buttonText={t('Delete')}
            mutationFn={async () => {
              await adminApi.deleteSwagger(swaggerId);
              queryClient.invalidateQueries({ queryKey: ['admin-swaggers'] });
              navigate('/internal-admin/swaggers');
            }}
            onError={(error) => {
              toast({
                title: t('Deletion failed'),
                description: serverMessage(
                  error,
                  t('Something went wrong. Please try again.'),
                ),
                variant: 'destructive',
              });
            }}
          >
            <Button variant="destructive" size="sm" disabled={isBusy}>
              <Trash2 className="mr-2 h-4 w-4" />
              {t('Delete')}
            </Button>
          </ConfirmationDeleteDialog>

          <Separator orientation="vertical" className="mx-1 h-6" />

          <Button size="sm" disabled={isBusy} onClick={() => save(undefined)}>
            {/* Label and spinner are always laid out (stacked in the same
                grid cell) and crossfade, instead of the spinner replacing
                the label and shrinking the button. */}
            <span className="grid">
              <span
                className={cn(
                  'col-start-1 row-start-1 transition-opacity duration-200 ease-out',
                  isSaving ? 'opacity-0' : 'opacity-100',
                )}
                aria-hidden={isSaving}
              >
                {t('Save')}
              </span>
              <span
                className={cn(
                  'col-start-1 row-start-1 flex items-center justify-center transition-opacity duration-200 ease-out',
                  isSaving ? 'opacity-100' : 'opacity-0',
                )}
                aria-hidden={!isSaving}
              >
                <LoadingSpinner size={16} className="stroke-background" />
              </span>
            </span>
          </Button>
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-3">
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-base">{t('General')}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <SwaggerAudienceField
              audience={audience}
              onAudienceChange={setAudience}
              tenantIds={tenantIds}
              onTenantIdsChange={setTenantIds}
              knownClients={knownClients}
            />
            <div>
              <Label htmlFor="swagger-name">{t('Name')}</Label>
              <Input
                id="swagger-name"
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="swagger-description">{t('Description')}</Label>
              <Textarea
                id="swagger-description"
                minRows={2}
                maxRows={6}
                value={description}
                onChange={(event) => setDescription(event.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="swagger-logo">{t('Icon URL')}</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="swagger-logo"
                  placeholder="https://…/logo.svg"
                  value={logoUrl}
                  onChange={(event) => setLogoUrl(event.target.value)}
                />
                {logoUrl.trim().length > 0 && (
                  <img
                    src={logoUrl}
                    alt=""
                    className="size-8 rounded border object-contain"
                  />
                )}
              </div>
              <p className="mt-1.5 text-xs text-muted-foreground">
                {t('A generic HTTP icon is used when left empty.')}
              </p>
            </div>
            <div>
              <Label htmlFor="swagger-base-url">{t('Base URL')}</Label>
              <Input
                id="swagger-base-url"
                placeholder="https://api.example.com"
                className={cn(
                  'font-mono text-xs',
                  baseUrl.trim().length === 0 &&
                    'border-destructive focus-visible:ring-destructive',
                )}
                value={baseUrl}
                onChange={(event) => setBaseUrl(event.target.value)}
              />
              <p className="mt-1.5 text-xs text-muted-foreground">
                {baseUrl.trim().length === 0
                  ? t(
                      'Empty — the document declared no server. Every action will fail until this is set.',
                    )
                  : t(
                      'Prepended to every route. A refresh from source never overwrites this.',
                    )}
              </p>
            </div>
            <div className="divide-y border-t pt-2">
              <InfoLine label={t('Piece Name')}>
                <span className="inline-flex items-center gap-1.5">
                  <span className="font-mono text-xs">
                    {definition.pieceName}
                  </span>
                  <CopyButton
                    textToCopy={definition.pieceName}
                    variant="ghost"
                    size="icon"
                    className="h-5 w-5"
                    withoutTooltip
                  />
                </span>
              </InfoLine>
              <InfoLine label={t('Version')}>{definition.version}</InfoLine>
              <InfoLine label={t('Source specification')}>
                {definition.originalSpecVersion}
              </InfoLine>
              {definition.sourceUrl !== null && (
                <InfoLine label={t('Source')}>
                  <span className="font-mono text-xs">
                    {definition.sourceUrl}
                  </span>
                </InfoLine>
              )}
              <InfoLine label={t('Last import')}>
                {formatUtils.formatDateToAgo(
                  new Date(definition.lastImportedAt),
                )}
              </InfoLine>
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">{t('Exposed routes')}</CardTitle>
          </CardHeader>
          <CardContent>
            <SwaggerRoutesPicker
              operations={definition.operations}
              selected={selected}
              onChange={setSelected}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminSwaggerDetailPage;
